import { useState } from 'react';
import {
  BadgeCheck,
  ExternalLink,
  FileText,
  Fingerprint,
  IdCard,
  Shield,
  User,
  X,
} from 'lucide-react';
import {
  formatCertDate,
  getCertStatus,
  IDENTITY_VAULT_FIELDS,
  MODAL_CERT_OPTIONS,
} from './mockDriverErpData';

const DOSSIER_TABS = [
  { id: 'identity', label: 'Identity Profile', icon: User },
  { id: 'vault', label: 'Document Vault', icon: IdCard },
  { id: 'certs', label: 'Certifications', icon: Shield },
];

function MetaCell({ label, value }) {
  return (
    <div className="rounded-lg border border-logi-grid/80 bg-white px-3 py-2.5">
      <p className="text-[9px] font-bold uppercase tracking-widest text-logi-text0">{label}</p>
      <p className="mt-1 text-sm font-medium text-logi-text">{value || '—'}</p>
    </div>
  );
}

function VaultDocRow({ label, attachment }) {
  if (!attachment) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-dashed border-logi-grid bg-white px-3 py-2">
        <span className="text-xs text-logi-text0">{label}</span>
        <span className="text-[10px] font-semibold uppercase text-logi-muted">Missing</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-logi-grid/80 bg-white px-3 py-2">
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-logi-text0">{label}</p>
        <p className="truncate font-mono text-xs text-violet-300">{attachment.fileName}</p>
      </div>
      <a
        href={attachment.mockUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.preventDefault()}
        className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-violet-500/30 bg-logi-accent/10 px-2 py-1 text-[10px] font-bold text-violet-300 transition hover:bg-violet-500/20"
      >
        <FileText className="h-3 w-3" />
        View PDF
      </a>
    </div>
  );
}

export default function DriverDossierDrawer({ driver, isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('identity');

  if (!isOpen || !driver) return null;

  const activeCerts = MODAL_CERT_OPTIONS.filter(
    ({ key }) => driver.certifications?.[key],
  );
  const vaultCount = Object.values(driver.identityVault || {}).filter(Boolean).length;
  const certDocCount = Object.values(driver.certAttachments || {}).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end animate-[fadeSlideIn_0.2s_ease-out]">
      <button
        type="button"
        aria-label="Close dossier backdrop"
        className="absolute inset-0 bg-white/75 backdrop-blur-sm"
        onClick={onClose}
      />

      <aside
        className="relative flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-violet-500/20 bg-white shadow-2xl shadow-violet-500/10 ring-1 ring-violet-500/25"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dossier-title"
      >
        <div className="relative border-b border-logi-grid bg-gradient-to-br from-logi-silver via-white to-logi-silver px-5 pb-5 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-lg border border-logi-grid bg-white p-2 text-logi-muted transition hover:border-red-500/40 hover:bg-red-50 hover:text-red-700"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>

          <div className="flex flex-col items-center pt-2 text-center">
            <div className="relative mb-3">
              {driver.profilePhoto?.previewUrl ? (
                <img
                  src={driver.profilePhoto.previewUrl}
                  alt={driver.name}
                  className="h-28 w-28 rounded-full object-cover ring-4 ring-logi-accent/30 ring-offset-2 ring-offset-white"
                />
              ) : (
                <div className="flex h-28 w-28 items-center justify-center rounded-full bg-white ring-4 ring-logi-grid ring-offset-2 ring-offset-white">
                  <User className="h-12 w-12 text-logi-text0" />
                </div>
              )}
              <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/20 ring-2 ring-emerald-500/40">
                <Fingerprint className="h-3.5 w-3.5 text-emerald-700" />
              </span>
            </div>

            <p className="text-[10px] font-bold uppercase tracking-widest text-logi-accent/80">
              Security Clearing Dossier
            </p>
            <h3 id="dossier-title" className="text-xl font-bold text-logi-text">
              {driver.name}
            </h3>
            <p className="font-mono text-xs text-logi-text0">{driver.id}</p>

            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <span className="rounded-full bg-logi-accent/10 px-2.5 py-0.5 text-[10px] font-bold text-violet-300 ring-1 ring-logi-accent/30">
                {driver.nationality}
              </span>
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-500/30">
                {activeCerts.length} Active Certs
              </span>
              <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-bold text-logi-muted ring-1 ring-logi-grid">
                {vaultCount + certDocCount} Documents
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-1 border-b border-logi-grid bg-white px-3 py-2">
          {DOSSIER_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] font-bold uppercase tracking-wide transition ${
                  isActive
                    ? 'bg-logi-accent/10 text-logi-accent ring-1 ring-logi-accent/30'
                    : 'text-logi-text0 hover:bg-logi-silver/80 hover:text-logi-muted'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'identity' && (
            <div className="space-y-3 animate-[fadeSlideIn_0.2s_ease-out]">
              <div className="mb-2 flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-logi-accent" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-logi-muted">
                  Personal Identity Metadata
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <MetaCell label="Full Legal Name" value={driver.name} />
                <MetaCell label="Nationality" value={driver.nationality} />
                <MetaCell label="Mobile" value={driver.mobile} />
                <MetaCell label="Emirates ID" value={driver.emiratesId} />
                <MetaCell label="Passport No." value={driver.passport} />
                <MetaCell label="Visa Status" value={driver.visa} />
                <MetaCell label="HV License No." value={driver.drivingLicenseNumber} />
                <MetaCell label="License Expiry" value={driver.heavyVehicleLicense} />
              </div>
            </div>
          )}

          {activeTab === 'vault' && (
            <div className="space-y-2 animate-[fadeSlideIn_0.2s_ease-out]">
              <p className="mb-3 text-[10px] leading-relaxed text-logi-text0">
                Identity document vault — RTA / Dubai Customs ready retrieval links.
              </p>
              {IDENTITY_VAULT_FIELDS.map(({ key, label }) => (
                <VaultDocRow
                  key={key}
                  label={label}
                  attachment={driver.identityVault?.[key]}
                />
              ))}
              {driver.profilePhoto && (
                <VaultDocRow
                  label="Biometric Profile Photo"
                  attachment={{
                    fileName: driver.profilePhoto.fileName,
                    mockUrl: driver.profilePhoto.mockUrl,
                  }}
                />
              )}
            </div>
          )}

          {activeTab === 'certs' && (
            <div className="space-y-3 animate-[fadeSlideIn_0.2s_ease-out]">
              <p className="mb-1 text-[10px] leading-relaxed text-logi-text0">
                Active certification checklist with digital certificate retrieval.
              </p>
              {activeCerts.length === 0 ? (
                <p className="rounded-lg border border-dashed border-logi-grid py-8 text-center text-xs text-logi-text0">
                  No active certifications on file
                </p>
              ) : (
                activeCerts.map(({ key, label, shortLabel }) => {
                  const expiry = driver.certifications[key];
                  const status = getCertStatus(expiry);
                  const attachment = driver.certAttachments?.[key];
                  const isValid = status.tier === 'valid';

                  return (
                    <div
                      key={key}
                      className={`rounded-xl border p-3 ${
                        isValid
                          ? 'border-emerald-200 bg-emerald-50'
                          : status.tier === 'critical'
                            ? 'border-red-200 bg-red-50'
                            : 'border-amber-200 bg-amber-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-md font-mono text-[10px] font-bold ${
                              isValid
                                ? 'bg-emerald-500/20 text-emerald-700 ring-1 ring-emerald-500/40'
                                : 'bg-white text-logi-muted ring-1 ring-logi-grid'
                            }`}
                          >
                            {shortLabel}
                          </span>
                          <div>
                            <p className="text-xs font-semibold text-white">{label}</p>
                            <p className="text-[10px] text-logi-text0">
                              Exp {formatCertDate(expiry)}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ring-1 ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </div>

                      {attachment ? (
                        <a
                          href={attachment.mockUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.preventDefault()}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-50 px-3 py-1.5 text-[11px] font-bold text-emerald-700 transition hover:bg-emerald-500/20"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          View PDF
                          <ExternalLink className="h-3 w-3 opacity-70" />
                        </a>
                      ) : (
                        <p className="mt-2 text-[10px] italic text-logi-text0">
                          Certificate file not yet uploaded
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
